import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { ADMIN, INVITE } from '@/constants/testIds';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import { Loader2, Users, Star, Calendar, Briefcase, BadgeCheck, ShieldCheck, Ticket, IndianRupee, Check, X, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { toast } from 'sonner';

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className="glass rounded-2xl p-6 border border-white/10 relative overflow-hidden">
    <div className={`absolute -right-6 -top-6 h-24 w-24 rounded-full ${color} opacity-30 blur-2xl`} />
    <Icon className="h-5 w-5 text-slate-300 mb-3" />
    <div className="font-display text-3xl font-bold text-white">{value ?? '—'}</div>
    <div className="text-xs text-slate-400 uppercase tracking-widest mt-1">{label}</div>
  </div>
);

const Admin = () => {
  const [stats, setStats] = useState(null);
  const [artists, setArtists] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [kyc, setKyc] = useState([]);
  const [referrals, setReferrals] = useState({ top_referrers: [], total_referred_signups: 0 });
  const [socialVerifs, setSocialVerifs] = useState([]);
  const [rejectReason, setRejectReason] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, b, k, r, sv] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/artists'),
        api.get('/admin/bookings'),
        api.get('/admin/kyc'),
        api.get('/admin/referrals'),
        api.get('/admin/social-verifications'),
      ]);
      setStats(s.data); setArtists(a.data.artists); setBookings(b.data.bookings); setKyc(k.data.submissions);
      setReferrals(r.data);
      setSocialVerifs(sv.data.submissions);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const verify = async (artist_id, level) => {
    const fd = new FormData(); fd.append('level', level);
    await api.patch(`/admin/artists/${artist_id}/verify`, fd);
    toast.success('Verification updated');
    load();
  };

  const reviewKyc = async (kyc_id, decision) => {
    const fd = new FormData();
    fd.append('decision', decision);
    if (decision === 'rejected') fd.append('reason', rejectReason[kyc_id] || 'Documents unclear');
    await api.patch(`/admin/kyc/${kyc_id}`, fd);
    toast.success(`KYC ${decision}`);
    load();
  };

  if (loading) return <div className="pt-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-fuchsia-400" /></div>;

  return (
    <div data-testid={ADMIN.root} className="pt-28 pb-24 max-w-7xl mx-auto px-6 sm:px-12">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-fuchsia-300 mb-3">Mission control</div>
        <h1 className="font-display text-4xl font-bold text-white tracking-tight">Admin <span className="rainbow-text">dashboard</span></h1>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-12">
        <Stat icon={Users} label="Users" value={stats?.users} color="bg-fuchsia-500" />
        <Stat icon={Star} label="Artists" value={stats?.artists} color="bg-amber-400" />
        <Stat icon={Briefcase} label="Bookings" value={stats?.bookings} color="bg-cyan-500" />
        <Stat icon={Calendar} label="Events" value={stats?.events} color="bg-emerald-500" />
        <Stat icon={Briefcase} label="Castings" value={stats?.castings} color="bg-purple-500" />
        <Stat icon={Star} label="Reviews" value={stats?.reviews} color="bg-rose-500" />
        <Stat icon={ShieldCheck} label="KYC pending" value={stats?.kyc_pending} color="bg-orange-500" />
        <Stat icon={Ticket} label="Tickets sold" value={stats?.tickets_sold} color="bg-cyan-500" />
        <Stat icon={IndianRupee} label="Revenue (₹)" value={Math.round(stats?.revenue || 0).toLocaleString('en-IN')} color="bg-emerald-500" />
      </div>

      <Tabs defaultValue="kyc" className="space-y-6">
        <TabsList className="glass-strong border border-white/10 bg-transparent gap-1 p-1">
          <TabsTrigger value="kyc" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-slate-400 rounded-lg px-4 py-2">
            KYC Queue {stats?.kyc_pending > 0 && <span className="ml-2 text-[10px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded">{stats.kyc_pending}</span>}
          </TabsTrigger>
          <TabsTrigger value="artists" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-slate-400 rounded-lg px-4 py-2">Artists</TabsTrigger>
          <TabsTrigger value="bookings" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-slate-400 rounded-lg px-4 py-2">Bookings</TabsTrigger>
          <TabsTrigger value="referrals" data-testid={INVITE.adminTab} className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-slate-400 rounded-lg px-4 py-2">
            <Gift className="h-3.5 w-3.5 mr-1 text-fuchsia-300" /> Referrals
            {referrals.total_referred_signups > 0 && <span className="ml-2 text-[10px] bg-fuchsia-500/20 text-fuchsia-200 px-1.5 py-0.5 rounded">{referrals.total_referred_signups}</span>}
          </TabsTrigger>
          <TabsTrigger value="social" data-testid="admin-social-tab" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-slate-400 rounded-lg px-4 py-2">
            <ShieldCheck className="h-3.5 w-3.5 mr-1 text-emerald-300" /> Social
            {socialVerifs.length > 0 && <span className="ml-2 text-[10px] bg-emerald-500/20 text-emerald-200 px-1.5 py-0.5 rounded">{socialVerifs.length}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="kyc">
          <div className="space-y-3">
            {kyc.length === 0 ? (
              <div className="text-slate-400 text-sm glass rounded-2xl p-6 border border-white/10">No KYC submissions yet.</div>
            ) : kyc.map((k) => (
              <div key={k.kyc_id} className="glass rounded-2xl p-5 border border-white/10 flex flex-col md:flex-row gap-5">
                <div className="flex gap-3 shrink-0">
                  {k.document_url && <img src={k.document_url} alt="" className="h-24 w-32 object-cover rounded-lg border border-white/10" />}
                  {k.selfie_url && <img src={k.selfie_url} alt="" className="h-24 w-24 object-cover rounded-lg border border-white/10" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-medium">{k.full_name}</span>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/10 text-slate-300">{k.document_type}</span>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300">{k.status}</span>
                  </div>
                  <div className="text-xs text-slate-400 mb-2">{k.user_email} · doc: {k.document_number}</div>
                  {k.notes && <p className="text-sm text-slate-300 mb-2">{k.notes}</p>}
                  {k.status === 'pending' && (
                    <div className="flex flex-wrap gap-2 mt-3 items-center">
                      <Button onClick={() => reviewKyc(k.kyc_id, 'approved')} className="btn-ai text-white border-0 rounded-full px-4 py-2 h-auto text-sm"><Check className="h-3.5 w-3.5 mr-1" />Approve</Button>
                      <Input placeholder="Rejection reason…" value={rejectReason[k.kyc_id] || ''} onChange={(e) => setRejectReason((r) => ({ ...r, [k.kyc_id]: e.target.value }))} className="bg-white/5 border-white/10 max-w-xs text-sm h-9" />
                      <Button onClick={() => reviewKyc(k.kyc_id, 'rejected')} variant="outline" className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10 rounded-full px-4 py-2 h-auto text-sm bg-transparent"><X className="h-3.5 w-3.5 mr-1" />Reject</Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="artists">
          <div className="glass rounded-2xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-slate-400 text-[11px] uppercase tracking-widest">
                <tr><th className="text-left p-4">Artist</th><th className="text-left p-4">Categories</th><th className="text-left p-4">City</th><th className="text-left p-4">Verified</th><th className="text-left p-4">Action</th></tr>
              </thead>
              <tbody>
                {artists.map((a) => (
                  <tr key={a.artist_id} className="border-t border-white/5">
                    <td className="p-4 text-white">{a.stage_name}</td>
                    <td className="p-4 text-slate-300">{(a.categories || []).join(', ')}</td>
                    <td className="p-4 text-slate-300">{a.location_city}</td>
                    <td className="p-4"><span className="text-xs flex items-center gap-1 text-cyan-300 capitalize"><BadgeCheck className="h-3.5 w-3.5" />{a.verified_level || 'basic'}</span></td>
                    <td className="p-4">
                      <Select onValueChange={(v) => verify(a.artist_id, v)}>
                        <SelectTrigger data-testid={ADMIN.verifyButton} className="w-36 bg-white/5 border-white/10"><SelectValue placeholder="Change…" /></SelectTrigger>
                        <SelectContent className="glass-strong border-white/10 text-slate-200">
                          <SelectItem value="basic">Basic</SelectItem>
                          <SelectItem value="professional">Professional</SelectItem>
                          <SelectItem value="elite">Elite</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="bookings">
          <div className="glass rounded-2xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-slate-400 text-[11px] uppercase tracking-widest">
                <tr><th className="text-left p-4">Event</th><th className="text-left p-4">Date</th><th className="text-left p-4">Location</th><th className="text-left p-4">Email</th><th className="text-left p-4">Status</th></tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.booking_id} className="border-t border-white/5">
                    <td className="p-4 text-white">{b.event_type}</td>
                    <td className="p-4 text-slate-300">{b.event_date}</td>
                    <td className="p-4 text-slate-300">{b.location}</td>
                    <td className="p-4 text-slate-300">{b.client_email}</td>
                    <td className="p-4"><span className="text-xs uppercase tracking-wider text-amber-300">{b.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="referrals">
          <div className="mb-5 flex items-center gap-3">
            <div className="glass rounded-2xl p-4 border border-white/10 flex-1">
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-1">Total referred signups</div>
              <div className="font-display text-3xl text-white">{referrals.total_referred_signups}</div>
            </div>
            <div className="glass rounded-2xl p-4 border border-white/10 flex-1">
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-1">Unique referrers</div>
              <div className="font-display text-3xl text-white">{referrals.top_referrers.length}</div>
            </div>
          </div>
          <div data-testid={INVITE.adminTable} className="glass rounded-2xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-slate-400 text-[11px] uppercase tracking-widest">
                <tr>
                  <th className="text-left p-4">Referrer</th>
                  <th className="text-left p-4">Email</th>
                  <th className="text-left p-4">Role</th>
                  <th className="text-left p-4">Username</th>
                  <th className="text-right p-4">Signups</th>
                </tr>
              </thead>
              <tbody>
                {referrals.top_referrers.length === 0 ? (
                  <tr><td colSpan="5" className="p-8 text-center text-slate-400 text-sm">No referrals yet.</td></tr>
                ) : referrals.top_referrers.map((r) => (
                  <tr key={r.user_id} className="border-t border-white/5">
                    <td className="p-4 text-white flex items-center gap-2">
                      {r.picture
                        ? <img src={r.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
                        : <div className="h-7 w-7 rounded-full bg-gradient-to-br from-fuchsia-500 to-purple-700 flex items-center justify-center text-white text-xs font-bold">{(r.name || '?').charAt(0).toUpperCase()}</div>
                      }
                      {r.name || 'Anonymous'}
                    </td>
                    <td className="p-4 text-slate-300">{r.email}</td>
                    <td className="p-4 text-slate-300 capitalize">{(r.account_type || 'fan').replace('_', ' ')}</td>
                    <td className="p-4 text-fuchsia-200 font-mono text-xs">@{r.username}</td>
                    <td className="p-4 text-right font-display text-lg text-white">{r.signups}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="social">
          <div data-testid="admin-social-table" className="glass rounded-2xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-slate-400 text-[11px] uppercase tracking-widest">
                <tr><th className="text-left p-4">Artist</th><th className="text-left p-4">Platform</th><th className="text-left p-4">Declared</th><th className="text-left p-4">Screenshot</th><th className="text-right p-4">Action</th></tr>
              </thead>
              <tbody>
                {socialVerifs.length === 0 ? (
                  <tr><td colSpan="5" className="p-8 text-center text-slate-400 text-sm">No pending verifications.</td></tr>
                ) : socialVerifs.map((s) => (
                  <tr key={s.verification_id} className="border-t border-white/5">
                    <td className="p-4 text-white text-xs font-mono">{s.artist_id}</td>
                    <td className="p-4 text-slate-300 capitalize">{s.platform}</td>
                    <td className="p-4 text-slate-300">{Number(s.declared_count || 0).toLocaleString('en-IN')}</td>
                    <td className="p-4">
                      <a href={s.screenshot_url} target="_blank" rel="noopener noreferrer" className="text-cyan-300 underline text-xs">view</a>
                    </td>
                    <td className="p-4 text-right space-x-2">
                      <Button size="sm" data-testid={`admin-social-approve-${s.verification_id}`} onClick={async () => { await api.post(`/admin/social-verifications/${s.verification_id}/decision`, { decision: 'approve' }); load(); }} className="bg-emerald-500/20 text-emerald-200 border border-emerald-400/30 hover:bg-emerald-500/30">
                        <Check className="h-3 w-3 mr-1" /> Approve
                      </Button>
                      <Button size="sm" data-testid={`admin-social-reject-${s.verification_id}`} onClick={async () => { await api.post(`/admin/social-verifications/${s.verification_id}/decision`, { decision: 'reject' }); load(); }} className="bg-rose-500/20 text-rose-200 border border-rose-400/30 hover:bg-rose-500/30">
                        <X className="h-3 w-3 mr-1" /> Reject
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Admin;
